// Related tasks of the card: tasks sharing at least one link (deal, contact,
// company, document) with the current one. Each link is fetched with its own
// list query, results are merged by id — a task linked to several shared
// entities appears exactly once.
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  getTasksQueryKey,
  listTasks,
  type ListTasksFilters,
  type TaskItem,
} from '../api/tasks.client.js';

export interface RelatedTasksResult {
  tasks: TaskItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export function useRelatedTasks(task: TaskItem | null): RelatedTasksResult {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  const links = useMemo<ListTasksFilters[]>(() => {
    if (!task) return [];
    // The API does not include the document relation — only the scalar
    // documentId is present on the record.
    const documentId = task.document?.id ?? task.documentId;
    return [
      ...(task.deal ? [{ dealId: task.deal.id }] : []),
      ...(task.company ? [{ companyId: task.company.id }] : []),
      ...(task.contact ? [{ contactId: task.contact.id }] : []),
      ...(documentId ? [{ documentId }] : []),
    ];
  }, [task]);

  const queries = useQueries({
    queries: links.map((filter) => ({
      queryKey: getTasksQueryKey(workspaceId, filter),
      queryFn: () => listTasks(filter),
      enabled: scope !== null,
    })),
  });

  const tasks = useMemo(() => {
    const merged = new Map<string, TaskItem>();
    for (const query of queries) {
      for (const item of query.data ?? []) {
        if (item.id !== task?.id) merged.set(item.id, item);
      }
    }
    // Parallel query responses arrive in nondeterministic order — sort the
    // result so the newest tasks come first.
    return [...merged.values()].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [queries, task]);

  return {
    tasks,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    error: queries.find((query) => query.isError)?.error ?? null,
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}
